<template>
  <div v-if="useAsBackgroundImage" ref="container" :class="['image-background', noObjectFitSupportClass]">
    <img
      ref="image"
      :data-src="useLazyLoading && srcImage"
      :data-srcset="useLazyLoading && srcSetImages"
      :src="!useLazyLoading && srcImage"
      :srcset="!useLazyLoading && srcSetImages"
      :sizes="srcSetSizes"
      :alt="altText"
      :loading="useLazyLoading && lazyLoadingType"
      :width="imageWidth"
      :height="imageHeight"
      :class="['image-element']">
  </div>

  <img
    v-else
    ref="image"
    :data-src="useLazyLoading && srcImage"
    :data-srcset="useLazyLoading && srcSetImages"
    :src="!useLazyLoading && srcImage"
    :srcset="!useLazyLoading && srcSetImages"
    :sizes="srcSetSizes"
    :alt="altText"
    :loading="useLazyLoading && lazyLoadingType"
    :width="imageWidth"
    :height="imageHeight"
    :class="['image-element']">
</template>

<script>
import lozad from 'lozad'

export default {
  props: {
    images: {
      type: [String, Array],
      required: true
    },
    sizes: {
      type: Array,
      default: () => []
    },
    altText: {
      type: String,
      required: true
    },
    imageWidth: {
      type: [Number, String],
      required: false,
      default: 10
    },
    imageHeight: {
      type: [Number, String],
      required: false,
      default: 10

    },
    useLazyLoading: {
      type: Boolean,
      default: true
    },
    lazyLoadingType: {
      type: String,
      default: 'lazy'
    },
    useAsBackgroundImage: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    tempImagePath: './assets/images/'
  }),
  computed: {
    srcSetImages() {
      return this.isImagesArray ? this.images.map(image => {
        const imagePath = this.imagePath(image.src)

        return `${imagePath} ${image.size !== undefined ? image.size : ''}`.trim()
      }).join(', ')
        : null
    },
    srcSetSizes() {
      return this.sizes.length > 0 && this.sizes.map(size => {
        return size
      }).join(', ')
    },
    srcImage() {
      return this.isImagesArray ? this.images.filter((image) => { return image.default }) : this.images
    },
    isImagesArray() {
      return Array.isArray(this.images)
    },
    isLazyLoadingSupported() {
      return ('loading' in HTMLImageElement.prototype)
    },
    isObjectFitSupported() {
      return ('objectFit' in document.documentElement.style)
    },
    noObjectFitSupportClass() {
      return {
        'image-background--no-object-fit': !this.isObjectFitSupported
      }
    }
  },
  mounted() {
    this.chainOfInits()
  },
  updated() {
    this.chainOfInits(true)
  },
  methods: {
    imagePath(image) {
      return (image.includes('http') || image.includes('//')) ? image : this.tempImagePath + image
    },
    lazyLoadingInit() {
      const image = this.$refs.image

      const srcset = image.dataset.srcset
      if (srcset) {
        this.$refs.image.srcset = srcset
      }

      const src = image.dataset.src
      if (src) {
        this.$refs.image.src = src
      }
    },
    noObjectFitSupport() {
      const container = this.$refs.container
      const image = this.$refs.image

      if (container !== null) {
        container.style.backgroundImage = `url(${image.src})`
        container.style.backgroundSize = this.lazyLoadingType
      }
    },
    chainOfInits(sourceUpdated) {
      if (this.useLazyLoading) {
        if (this.isLazyLoadingSupported) {
          this.lazyLoadingInit()
        } else if (!this.isLazyLoadingSupported && sourceUpdated) {
          this.lazyLoadingInit()
        } else if (!this.isLazyLoadingSupported) {
          const observer = lozad(this.$refs.image)
          observer.observe()
        }
      }

      if (!this.isObjectFitSupported) {
        this.noObjectFitSupport()
      }
    }
  }
}
</script>

<style lang="scss">
.image-element {
  display: block;
  height: auto;
}

.image-background {
  position: relative;
}

.image-background .image-element {
  object-fit: cover;
  object-position: center;
}

.image-background--no-object-fit {
  background-position: center;
  background-repeat: no-repeat;
}

.image-background--no-object-fit .image-element {
  display: none;
}
</style>
